import React, { useEffect, useRef } from 'react';

function Menu({ anchorEl, anchorOrigin, anchorRef, children, className = '', onClose, open, style, transformOrigin, ...props }) {
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const getEnabledItems = () => (
      [...(menuRef.current?.querySelectorAll('[role="menuitem"]') || [])]
        .filter((item) => !item.disabled && item.getAttribute('aria-disabled') !== 'true')
    );
    const focusItem = (index) => {
      const items = getEnabledItems();
      if (!items.length) {
        return;
      }

      items[(index + items.length) % items.length].focus();
    };
    const focusAnchor = () => {
      const anchor = anchorRef?.current || anchorEl;
      if (!anchor) {
        return;
      }

      if (typeof anchor.focus === 'function' && anchor.tabIndex >= 0) {
        anchor.focus();
        return;
      }

      anchor.querySelector?.('button, [href], [tabindex]:not([tabindex="-1"])')?.focus?.();
    };
    const handleKeyDown = (event) => {
      const items = getEnabledItems();
      const currentIndex = items.indexOf(document.activeElement);

      if (event.key === 'Escape') {
        event.preventDefault();
        onClose?.();
        focusAnchor();
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        focusItem(currentIndex >= 0 ? currentIndex + 1 : 0);
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        focusItem(currentIndex >= 0 ? currentIndex - 1 : items.length - 1);
        return;
      }

      if (event.key === 'Home') {
        event.preventDefault();
        focusItem(0);
        return;
      }

      if (event.key === 'End') {
        event.preventDefault();
        focusItem(items.length - 1);
        return;
      }

      if (event.key === 'Tab') {
        onClose?.();
      }
    };
    const handleMouseDown = (event) => {
      if (
        menuRef.current
        && !menuRef.current.contains(event.target)
        && !(anchorRef?.current || anchorEl)?.contains(event.target)
      ) {
        onClose?.();
      }
    };

    window.setTimeout(() => focusItem(0), 0);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [anchorEl, anchorRef, onClose, open]);

  if (!open) return null;

  const anchorRect = anchorEl?.getBoundingClientRect?.();
  const estimatedWidth = 220;
  const horizontalAnchor = anchorOrigin?.horizontal || 'left';
  const horizontalTransform = transformOrigin?.horizontal || 'left';
  const verticalAnchor = anchorOrigin?.vertical || 'bottom';
  const anchorX = horizontalAnchor === 'right' ? anchorRect?.right : anchorRect?.left;
  const rawLeft = anchorX == null
    ? undefined
    : anchorX - (horizontalTransform === 'right' ? estimatedWidth : 0);
  const left = rawLeft == null
    ? undefined
    : Math.max(8, Math.min(rawLeft, window.innerWidth - estimatedWidth - 8));
  const anchorY = anchorRect ? (verticalAnchor === 'top' ? anchorRect.top : anchorRect.bottom) : undefined;
  const opensUp = anchorRect && anchorY + 320 > window.innerHeight && anchorRect.top > 320;
  const top = opensUp ? undefined : anchorY;
  const bottom = opensUp ? window.innerHeight - anchorRect.top : undefined;
  const positionedStyle = anchorRect ? { position: 'fixed', left, top, bottom, maxHeight: 'calc(100vh - 16px)', overflowY: 'auto', ...style } : style;

  return (
    <div className={['custom-menu', className].filter(Boolean).join(' ')} ref={menuRef} role="menu" style={positionedStyle} {...props}>
      {children}
    </div>
  );
}

export function MenuItem({ children, className = '', component: Component = 'button', danger = false, disabled = false, onClick, ...props }) {
  return (
    <Component
      className={['custom-menu__item', danger ? 'danger' : '', className].filter(Boolean).join(' ')}
      disabled={disabled}
      onClick={onClick}
      role="menuitem"
      type={Component === 'button' ? 'button' : undefined}
      {...props}
    >
      {children}
    </Component>
  );
}

export function MenuGroup({ children, label }) {
  return (
    <div className="custom-menu__group">
      {label && <span className="custom-menu__label">{label}</span>}
      {children}
    </div>
  );
}

export default Menu;
